import useLinearLightErrorNotice from '../../utils/linearLight/useErrorNotice';
import { useContext } from 'react';
import { LinearLightEditorContext } from '../../utils/linearLight/editor';
import LinearLightLayer from '../../utils/linearLight/layer';
import { isLinearLightBlendMode } from '../../utils/linearLight/source';
/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import styled, { css } from 'styled-components';
import { useUnits } from '@web-stories-wp/units';
import { StoryAnimation } from '@web-stories-wp/animation';
import { useStory } from '@web-stories-wp/story-editor';

/**
 * Internal dependencies
 */
import { isEqual } from '../../../../../utils/isEqual';
import { elementIs, getDefinitionForType } from '../../elements';
import {
  elementWithPosition,
  elementWithRotation,
  elementWithSize,
} from '../../elements/shared';
import WithMask from '../../masks/display';
import StoryPropTypes from '../../types';
import { useTransformHandler } from '../transform';
import useColorTransformHandler from '../../elements/shared/useColorTransformHandler';
import {
  getBorderPositionCSS,
  getBorderRadius,
  getResponsiveBorder,
  shouldDisplayBorder,
} from '../../utils/elementBorder';
import {
  getOverlayFilterStyle,
  getOverlayRenderStack,
} from '../../utils/overlayStack';
import { getElementBlendMode } from '../../elements/shared/blendModes';
import { TextBoxBackdropGroup } from '../../elements/text/textBoxBackdrop';
import { getTextBoxBackdropStyles } from '../../elements/text/getTextBoxBackdropStyles';
import {
  getElementBackdropStyles,
  hasElementBackdrop,
} from '../../elements/shared/getElementBackdropStyles';
import ElementBackdrop from '../../elements/shared/elementBackdrop';
import { getTextShadowStyles } from '../../elements/text/getTextShadowStyles';
import getTextHeightResizeUpdates from '../../utils/getTextHeightResizeUpdates';
import { isTextBoxAttachment } from '../../utils/textBoxAttachment';
import TextBoxAttachmentTransform from './useTextBoxAttachmentTransform';

// Using attributes to avoid creation of hundreds of classes by styled components for previewMode.
const Wrapper = styled.div.attrs(
  ({ previewMode, x, y, width, height, rotationAngle, $blendMode }) => {
    const style = {
      position: 'absolute',
      zIndex: 1,
      left: `${x}px`,
      top: `${y}px`,
      width: `${width}px`,
      height: `${height}px`,
      transform: `rotate(${rotationAngle}deg)`,
      ...($blendMode ? { mixBlendMode: $blendMode } : null),
    };
    return previewMode ? { style } : {};
  }
)`
  ${({ previewMode }) => !previewMode && elementWithPosition}
  ${({ previewMode }) => !previewMode && elementWithSize}
  ${({ previewMode }) => !previewMode && elementWithRotation}
  ${({ $blendMode }) =>
    $blendMode &&
    css`
      mix-blend-mode: ${$blendMode};
    `}
  contain: layout;
  transition: opacity 0.15s cubic-bezier(0, 0, 0.54, 1);

  ${({ isBackground, theme }) =>
    isBackground &&
    css`
      border-radius: ${theme.borders.radius.small};
      overflow: hidden;
    `}
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

function BackgroundFilterOverlay({ box, elementId, filter }) {
  const overlayRef = useRef(null);
  useColorTransformHandler({
    id: elementId,
    targetRef: overlayRef,
    expectedFilterId: filter.id,
    fallbackColor: filter.pattern,
    fallbackBlur: filter.blur,
    resetOnNullTransform: false,
    useOverlayFilterStyle: true,
  });

  return (
    <BackgroundOverlay
      ref={overlayRef}
      style={getOverlayFilterStyle(filter, box)}
    />
  );
}

BackgroundFilterOverlay.propTypes = {
  box: PropTypes.object.isRequired,
  elementId: PropTypes.string.isRequired,
  filter: PropTypes.object.isRequired,
};

const ReplacementContainer = styled.div`
  transition: opacity 0.25s cubic-bezier(0, 0, 0.54, 1);
  pointer-events: none;
  opacity: ${({ hasReplacement }) => (hasReplacement ? 1 : 0)};
  height: 100%;
`;

function AnimationWrapper({ children, id, isAnimatable }) {
  return isAnimatable ? (
    <StoryAnimation.WAAPIWrapper target={id}>
      {children}
    </StoryAnimation.WAAPIWrapper>
  ) : (
    children
  );
}
AnimationWrapper.propTypes = {
  isAnimatable: PropTypes.bool.isRequired,
  children: PropTypes.arrayOf(PropTypes.node),
  id: PropTypes.string,
};

function DisplayElementContent({
  element: elementWithAllProperties,
  previewMode,
  isAnimatable = false,
  shouldPlayElementAnimation = false,
  ...rest
}) {
  const element = useMemo(() => {
    if (!elementWithAllProperties.resource) {
      return elementWithAllProperties;
    }

    const { isMuted, ...resource } = elementWithAllProperties.resource;
    return {
      ...elementWithAllProperties,
      resource,
    };
  }, [elementWithAllProperties]);

  const { getBox, dataToEditorX, dataToEditorY } = useUnits((state) => ({
    getBox: state.actions.getBox,
    dataToEditorX: state.actions.dataToEditorX,
    dataToEditorY: state.actions.dataToEditorY,
  }));

  const updateElementsById = useStory(
    ({ actions }) => actions.updateElementsById
  );

  const [replacement, setReplacement] = useState(null);

  const hasReplacement = Boolean(replacement);

  const {
    id,
    blendMode,
    opacity,
    type,
    isBackground,
    isHidden,
    overlay,
    overlays,
    border = {},
    flip,
  } = element;

  const replacementElement = hasReplacement
    ? {
        ...element,
        type: replacement.resource.type,
        resource: replacement.resource,
        scale: replacement.scale,
        focalX: replacement.focalX,
        focalY: replacement.focalY,
        // Okay, this is a bit weird, but... the flip and overlay properties are taken from the dragged image
        // if the drop-target is the background element, but from the original drop-target image
        // itself if the drop-target is a regular element.
        //
        // @see compare with similar logic in `combineElements`
        flip: isBackground ? replacement.flip : flip,
        overlay: isBackground ? replacement.overlay : overlay,
        overlays: isBackground ? replacement.overlays : overlays,
      }
    : null;

  const { Display } = getDefinitionForType(type);
  const { Display: Replacement } =
    getDefinitionForType(replacement?.resource.type) || {};

  const wrapperRef = useRef(null);
  const backdropTintRef = useRef(null);

  const box = getBox(element);
  const responsiveBorder = getResponsiveBorder(
    border,
    previewMode,
    dataToEditorX
  );
  const elementBlendMode = getElementBlendMode(blendMode);
  const textBoxBackdropStyle = useMemo(
    () =>
      elementIs.text(element)
        ? getTextBoxBackdropStyles({
            element,
            dataToStyleX: (value) => `${dataToEditorX(value)}px`,
            dataToStyleY: (value) => `${dataToEditorY(value)}px`,
            dataToBlur: (value) => `${dataToEditorX(value)}px`,
            disableBlur:
              Boolean(elementBlendMode) ||
              isLinearLightBlendMode(element.blendMode),
          })
        : null,
    [dataToEditorX, dataToEditorY, element, elementBlendMode]
  );
  const elementBackdropStyle = useMemo(
    () =>
      getElementBackdropStyles({
        element: element,
        offsetForBorderFlip: true,
        dataToStyleX: (value) => `${dataToEditorX(value)}px`,
        dataToStyleY: (value) => `${dataToEditorY(value)}px`,
      }),
    [element, dataToEditorX, dataToEditorY]
  );
  const hasBackdrop = Boolean(textBoxBackdropStyle || elementBackdropStyle);
  const textBoxShadowStyle = elementIs.text(element)
    ? getTextShadowStyles({
        element,
        dataToStyleX: (value) => dataToEditorX(value),
        dataToStyleY: (value) => dataToEditorY(value),
      })
    : null;
  const backdropCompositeStyle = hasBackdrop
    ? {
        ...(typeof opacity !== 'undefined' ? { opacity: opacity / 100 } : null),
        ...(elementBlendMode ? { mixBlendMode: elementBlendMode } : null),
      }
    : null;
  const textBoxShadowLayerStyle =
    hasBackdrop && textBoxShadowStyle?.boxShadow
      ? {
          ...(shouldDisplayBorder(element)
            ? getBorderPositionCSS({
                ...responsiveBorder,
                width: `${box.width}px`,
                height: `${box.height}px`,
              })
            : { inset: 0 }),
          ...getBorderRadius(element),
          ...backdropCompositeStyle,
          boxShadow: textBoxShadowStyle.boxShadow,
        }
      : null;

  useColorTransformHandler({
    id,
    targetRef: textBoxBackdropStyle ? backdropTintRef : null,
    expectedStyle: 'background',
    fallbackColor: element.backgroundColor,
  });

  useTransformHandler(id, (transform) => {
    const target = wrapperRef.current;
    if (!target) {
      return;
    }
    if (transform === null) {
      target.style.transform = '';
      target.style.width = '';
      target.style.height = '';
    } else {
      const { translate, rotate, resize, dropTargets } = transform;
      const hasGeometry = translate || typeof rotate !== 'undefined' || resize;
      if (!hasGeometry && !dropTargets) {
        return;
      }

      if (hasGeometry) {
        const [translateX = 0, translateY = 0] = translate || [];
        const rotation =
          typeof rotate === 'number' ? rotate : box.rotationAngle || 0;
        target.style.transform = `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg)`;
        if (resize && resize[0] !== 0 && resize[1] !== 0) {
          target.style.width = `${resize[0]}px`;
          target.style.height = `${resize[1]}px`;
        }
      }
      if (dropTargets?.hover !== undefined) {
        target.style.opacity = dropTargets.hover ? 0 : 1;
      }
      if (dropTargets?.replacement !== undefined) {
        setReplacement(dropTargets.replacement || null);
      }
    }
  });

  const overlayFilters = getOverlayRenderStack(element);

  const handleTextDimensionsUpdated = useCallback(
    ({ height, width }) => {
      updateElementsById({
        elementIds: [id],
        properties: (currentProperties) => {
          const geometry = getTextHeightResizeUpdates(
            currentProperties,
            height
          );
          return {
            ...currentProperties,
            ...geometry,
            width,
            lastUpdatedBy: 'user',
          };
        },
      });
    },
    [id, updateElementsById]
  );

  if (isHidden) {
    return null;
  }

  const foregroundElement = (
    <WithMask
      element={element}
      fill
      box={box}
      style={{
        ...(hasBackdrop
          ? backdropCompositeStyle
          : {
              opacity: typeof opacity !== 'undefined' ? opacity / 100 : null,
            }),
        ...(shouldDisplayBorder(element)
          ? getBorderPositionCSS({
              ...responsiveBorder,
              width: `${box.width}px`,
              height: `${box.height}px`,
            })
          : null),
      }}
      previewMode={previewMode}
    >
      <Display
        element={element}
        previewMode={previewMode}
        box={box}
        isAnimatable={isAnimatable}
        isAnimationPlaying={shouldPlayElementAnimation}
        handleDimensionsUpdated={
          elementIs.text(element) ? handleTextDimensionsUpdated : undefined
        }
        {...(elementIs.text(element)
          ? { hasTextBoxBackdrop: hasBackdrop }
          : null)}
      />
    </WithMask>
  );

  return (
    <Wrapper
      ref={wrapperRef}
      data-element-id={id}
      isBackground={element.isBackground}
      previewMode={previewMode}
      $blendMode={hasBackdrop ? null : elementBlendMode}
      {...box}
    >
      {!previewMode && isTextBoxAttachment(element.textBoxAttachment) && (
        <TextBoxAttachmentTransform element={element} />
      )}
      <AnimationWrapper id={id} isAnimatable={isAnimatable}>
        {textBoxBackdropStyle ? (
          <TextBoxBackdropGroup
            element={element}
            style={{
              ...textBoxBackdropStyle,
              ...backdropCompositeStyle,
            }}
            shadowStyle={textBoxShadowLayerStyle}
            tintRef={backdropTintRef}
          >
            {foregroundElement}
          </TextBoxBackdropGroup>
        ) : (
          <>
            {elementBackdropStyle && (
              <ElementBackdrop element={element} style={elementBackdropStyle} />
            )}
            {foregroundElement}
          </>
        )}
        {!previewMode && (
          <ReplacementContainer hasReplacement={hasReplacement}>
            {replacementElement && (
              <WithMask
                element={replacementElement}
                fill
                box={box}
                style={{
                  opacity: opacity ? opacity / 100 : null,
                }}
                previewMode={previewMode}
              >
                <Replacement
                  element={replacementElement}
                  box={box}
                  isAnimationPlaying={shouldPlayElementAnimation}
                />
              </WithMask>
            )}
          </ReplacementContainer>
        )}
        {isBackground &&
          !hasReplacement &&
          overlayFilters.map((filter) => (
            <BackgroundFilterOverlay
              key={filter.id}
              box={box}
              elementId={id}
              filter={filter}
            />
          ))}
      </AnimationWrapper>
    </Wrapper>
  );
}

function DisplayElement(props) {
  const { canvasSize, getBox } = useUnits(({ state, actions }) => ({
    canvasSize: state?.pageSize,
    getBox: actions.getBox,
  }));
  const onError = useLinearLightErrorNotice(
    hasElementBackdrop(props.element) ? 'Background blur' : 'Linear Light'
  );
  const { element } = props;
  const { source } = useContext(LinearLightEditorContext);
  const sourceUri =
    props.isEditing && source?.id === element.id ? source.uri : undefined;
  if (
    !element.isHidden &&
    (hasElementBackdrop(element) ||
      isLinearLightBlendMode(element.blendMode)) &&
    ['image', 'text', 'shape'].includes(element.type)
  ) {
    return (
      <LinearLightLayer
        backdropElementId={element.id}
        paddingRatio={props.previewMode ? props.previewPaddingRatio : 0}
        combinedBlur={hasElementBackdrop(element)}
        blendMode={element.blendMode}
        cropToPaint={!hasElementBackdrop(element)}
        canvasSize={props.previewMode ? undefined : canvasSize}
        captureFonts={element.type === 'text'}
        elementBox={props.previewMode ? undefined : getBox(element)}
        isText={element.type === 'text'}
        opaqueBackdrop={props.opaqueBackdrop}
        onError={onError}
        opacity={element.opacity}
        stackLevel={1}
        sourceUri={sourceUri}
        sourceMask={sourceUri ? source.mask : undefined}
        sourcePaintMask={sourceUri ? source.paintMask : undefined}
        sourceBlur={sourceUri ? source.blur : undefined}
        sourceFeather={sourceUri ? source.feather : undefined}
        transformElementId={props.previewMode ? undefined : element.id}
        rotationAngle={element.rotationAngle}
      >
        <DisplayElementContent
          {...props}
          element={{
            ...element,
            opacity: 100,
            blendMode: hasElementBackdrop(element)
              ? 'normal'
              : element.blendMode,
          }}
        />
      </LinearLightLayer>
    );
  }
  return <DisplayElementContent {...props} />;
}

DisplayElement.propTypes = {
  previewMode: PropTypes.bool,
  previewPaddingRatio: PropTypes.number,
  opaqueBackdrop: PropTypes.bool,
  element: StoryPropTypes.element.isRequired,
  isAnimatable: PropTypes.bool,
  shouldPlayElementAnimation: PropTypes.bool,
};

function elementPropsAreEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps, ['isMuted']);
}

// Don't rerender the display element needlessly (e.g. element selection change)
// if the element or other props haven't changed.
export default memo(DisplayElement, elementPropsAreEqual);
